// Story 7.12 (ADO-911) — recommendations client (FR106 linked/related + FR107
// "Customers Who Have Bought").
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the
// SearchAndDiscovery recommendations read. Mirrors `substitutes.client.ts`: the
// SINGLE consumer seam until the `openapi-typescript`-generated client lands
// (7.1 D-6), exercised against msw in tests. The backend serves a bare
// `catalogue/products/:sku/recommendations` route (D-10); this client prefixes
// `API_PREFIX`.
//
// The wire shapes are a LOCK-STEP COPY of the module's exported
// `RecommendedProductDto` / `ProductRecommendationsDto` — keep the two in sync.
// PRICE IS DELIBERATELY ABSENT (Deviation D-1): a recommendation tile carries
// name / image / pack only — the Story 7.9/7.11 posture.

import { API_PREFIX, apiFetch } from './shoplink-api';
import { withEntry } from './catalogue-entry';

/** One recommended product (lock-step with `RecommendedProductDto`). Availability
 *  (Story 7.3) is composed client-side, as in browse/search. NO price. */
export interface RecommendedProduct {
  readonly sku: string;
  readonly name: string;
  /** Primary product image URL, or `null` → the branded placeholder (Story 7.4). */
  readonly imageUrl: string | null;
  readonly packSize: string | null;
  /** Picked fresh: no stock state, always orderable (Enabler E7-B, ADO-2273; MP AVL-5.4). The
   *  availability read leaves it out, so the card shows SL1.0's "Picked fresh" where the stock
   *  pill would be (Enabler E7-C, ADO-2274). Lock-step with the backend DTO. */
  readonly isPickedFresh: boolean;
}

/** Both rails for a product in ONE response (lock-step with `ProductRecommendationsDto`). */
export interface ProductRecommendations {
  readonly related: readonly RecommendedProduct[];
  readonly customersWhoBought: readonly RecommendedProduct[];
}

/** The bare backend segment (after `API_PREFIX`) for a product's recommendations. */
export function recommendationsPath(sku: string): string {
  return `${API_PREFIX}/catalogue/products/${encodeURIComponent(sku)}/recommendations`;
}

/**
 * Fetch both recommendation rails for `sku` in the selected ordering `entry`'s range
 * (Enabler E7-C, ADO-2274, D-1). The server returns `{ related, customersWhoBought }`,
 * capped; the related rail is the entry's depot's.
 * Either array can be empty — a product with no related products / no purchase
 * history yields `[]` for that rail (AC3 — the web renders no section). Only a
 * transport/non-2xx failure throws; an empty array is a valid, non-error result.
 */
export async function fetchRecommendations(
  entry: string,
  sku: string,
): Promise<ProductRecommendations> {
  const res = await apiFetch(withEntry(recommendationsPath(sku), entry));
  if (!res.ok) throw new Error(`Failed to load recommendations (${res.status})`);
  return (await res.json()) as ProductRecommendations;
}
